(() => {
  if (typeof window.FaraCartCount?.update !== 'function') {
    let revision = 0, appliedRevision = 0;
    const update = count => {
      if (!Number.isSafeInteger(count) || count < 0) return;
      document.querySelectorAll('[data-fara-cart-count]').forEach(node => { node.textContent = `(${count})`; });
      return true;
    };
    // StandardEvents.cartLinesUpdate: listen without importing another idle module.
    document.addEventListener('shopify:cart:lines-update', event => {
      if (!event.promise || typeof event.promise.then !== 'function') return;
      const attempt = ++revision;
      Promise.resolve(event.promise).then(result => {
        if (attempt > appliedRevision && update(result?.cart?.totalQuantity ?? result?.detail?.itemCount)) appliedRevision = attempt;
      }).catch(() => {});
    });
    window.FaraCartCount = { update };
  }
  const selector = '[data-fara-preview]';
  const readJSON = (value, fallback) => { try { return JSON.parse(value); } catch { return fallback; } };
  const labels = readJSON(document.getElementById('FaraPreviewLabels')?.textContent, {});
  const phrase = (name, title = '') => (labels[name] || '').replace('__title__', title);
  const catalogue = readJSON(document.getElementById('FaraRadioPlaylist')?.textContent, {});
  const archive = new Map((catalogue.tracks || []).map(track => [track[3], {
    url: new URL(track[4], catalogue.base).href, title: `${track[0]} — ${track[1]}`
  }]));
  const pools = new WeakMap();
  const initialLabels = new WeakMap();
  const lastChoices = new Map();
  let player, current, owner, playing = false, generation = 0;
  let resumeRadio = false, returningToRadio = false;
  const normalise = url => {
    try {
      const parsed = new URL(url, location.href);
      return /^https?:$/.test(parsed.protocol) ? {url: parsed.href, key: parsed.origin + parsed.pathname} : null;
    } catch { return null; }
  };
  const poolFor = button => {
    if (pools.has(button)) return pools.get(button);
    const baseLabel = button.dataset.previewBaseLabel ?? button.getAttribute('aria-label') ?? '';
    button.dataset.previewBaseLabel = baseLabel;
    initialLabels.set(button, baseLabel);
    let tracks = readJSON(button.dataset.previewTracks, []).filter(track => track?.url);
    if (!tracks.length && button.dataset.faraPreview) tracks = [{url: button.dataset.faraPreview, title: button.dataset.previewName || button.getAttribute('aria-label')}];
    if (!tracks.length) tracks = readJSON(button.dataset.previewProducts, []).map(handle => archive.get(handle)).filter(Boolean);
    const seen = new Set();
    const pool = tracks.flatMap(track => {
      const source = normalise(track.url);
      if (!source || seen.has(source.key)) return [];
      seen.add(source.key);
      return [{...source, title: track.title || button.dataset.previewName || ''}];
    });
    pools.set(button, pool);
    return pool;
  };
  const announce = (message, error = false) => {
    let status = document.getElementById('FaraPreviewStatus');
    if (!status) {
      status = document.createElement('p');
      status.id = 'FaraPreviewStatus';
      status.setAttribute('role', 'status');
      document.body.append(status);
    }
    const host = document.querySelector?.('dialog[open]') || document.body;
    if (status.parentElement !== host) host.append(status);
    status.className = error ? 'fara-preview-status is-error' : 'visually-hidden';
    status.textContent = message;
  };
  const sync = () => {
    document.querySelectorAll(selector).forEach(button => {
      const pool = poolFor(button);
      const matches = current && (button === owner || (pool.length === 1 && pool[0].key === current.key));
      const pressed = Boolean(playing && matches);
      button.setAttribute('aria-pressed', String(pressed));
      const label = button.querySelector('[data-preview-label]');
      if (label) label.textContent = pressed ? button.dataset.pauseLabel : button.dataset.playLabel;
      const icon = button.querySelector('[data-preview-icon]');
      if (icon && !icon.hasAttribute('data-preview-svg')) icon.textContent = pressed ? 'Ⅱ' : '▶';
      const accessibleName = matches ? phrase(pressed ? 'pause' : 'play', current.title) : initialLabels.get(button);
      if (accessibleName) { button.setAttribute('aria-label', accessibleName); button.title = accessibleName; }
    });
  };
  const stop = (clear = true, returnToRadio = false) => {
    generation++;
    playing = false;
    player?.pause();
    if (clear) { current = null; owner = null; announce(''); }
    sync();
    const shouldResume = returnToRadio && resumeRadio;
    resumeRadio = false;
    if (shouldResume && window.FaraRadio?.resume) {
      returningToRadio = true;
      Promise.resolve(window.FaraRadio.resume()).finally(() => { returningToRadio = false; });
    }
  };
  const fail = () => { stop(true, true); announce(labels.error || document.body.dataset.faraPreviewError || '', true); };
  const getPlayer = () => {
    if (player) return player;
    player = new Audio();
    player.preload = 'none';
    player.addEventListener('ended', () => stop(true, true));
    player.addEventListener('timeupdate', () => { if (current && player.currentTime >= 60) stop(true, true); });
    player.addEventListener('error', () => { if (current) fail(); });
    return player;
  };
  const revealImage = image => {
    if (image.dataset.faraSrcset) image.srcset = image.dataset.faraSrcset;
    image.src = image.dataset.faraSrc;
    image.removeAttribute('data-fara-srcset');
    image.removeAttribute('data-fara-src');
  };
  const imageObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      imageObserver.unobserve(entry.target);
      revealImage(entry.target);
    });
  }, {rootMargin: '100px 0px'}) : null;
  const prepare = () => {
    if (owner?.isConnected === false) stop(true, true);
    document.querySelectorAll(selector).forEach(button => { button.hidden = !poolFor(button).length; });
    imageObserver?.disconnect();
    document.querySelectorAll('img[data-fara-src]').forEach(image => {
      if (imageObserver) imageObserver.observe(image);
      else revealImage(image);
    });
    sync();
  };
  prepare();
  document.addEventListener('shopify:section:load', prepare);
  document.addEventListener('fara:preview-prepare', prepare);
  document.addEventListener('click', event => {
    const button = event.target.closest(selector);
    if (!button || button.disabled) return;
    const pool = poolFor(button);
    if (!pool.length) return;
    event.preventDefault();
    const sameSelection = current && (button === owner || (pool.length === 1 && pool[0].key === current.key));
    if (playing && sameSelection) { stop(false, true); announce(labels.paused || ''); return; }
    const audio = getPlayer();
    if (!sameSelection) {
      audio.pause();
      const selectionKey = button.dataset.previewArtist || pool.map(track => track.key).join('|');
      const last = lastChoices.get(selectionKey);
      const candidates = pool.length > 1 ? pool.filter(track => track.key !== last) : pool;
      current = candidates[Math.floor(Math.random() * candidates.length)];
      lastChoices.set(selectionKey, current.key);
      audio.src = current.url;
    }
    owner = button;
    const attempt = ++generation;
    resumeRadio = resumeRadio || Boolean(window.FaraRadio?.isPlaying?.());
    document.querySelectorAll('audio').forEach(element => { if (element !== audio) element.pause(); });
    window.dispatchEvent(new CustomEvent('sx:audio-play', {detail: {source: 'fara-preview'}}));
    playing = true;
    sync();
    announce(phrase('loading', current.title));
    audio.play().then(() => {
      if (attempt !== generation) return;
      announce(phrase('playing', current.title));
    }).catch(error => {
      if (attempt === generation && error.name !== 'AbortError') fail();
    });
  });
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-fara-variant]')) return;
    const section = event.target.closest('[data-fara-product]');
    const option = event.target.selectedOptions[0];
    const button = section.querySelector('[data-fara-add]');
    const unavailable = option.dataset.available !== 'true';
    section.querySelector('[data-fara-price]').textContent = option.dataset.price;
    button.disabled = unavailable || Boolean(event.target.closest('form')?.dataset.faraSubmitting);
    button.textContent = unavailable ? button.dataset.unavailableLabel : button.dataset.availableLabel + ' ';
    if (!unavailable) {
      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↗';
      button.append(arrow);
    }
  });
  window.addEventListener('pagehide', () => stop());
  window.addEventListener('sx:audio-play', event => {
    if (event.detail?.source === 'fara-preview') return;
    if (event.detail?.source === 'radio' && returningToRadio) {
      if (playing) window.FaraRadio?.pause?.();
      return;
    }
    stop();
  });
  document.addEventListener('shopify:section:unload', () => stop(true, true));
  window.addEventListener('fara:before-navigate', () => stop(true, true));
})();
